"use client";
import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";

export function IntegrationsCarousel() {
    const [isSafari] = useState(false);
    const imgData: { alt: string; uri: string }[] = [
        { alt: "Aptem LMS Logo", uri: "/lms/aptem.png" },
        { alt: "Blackboard Logo", uri: "/lms/bb.png" },
        { alt: "Brightspace Logo", uri: "/lms/brightspace.png" },
        { alt: "Canvas Logo", uri: "/lms/canvas_circle.png" },
        { alt: "Google Classroom Logo", uri: "/lms/gc.jpeg" },
        { alt: "D2L Logo", uri: "/lms/d2l.jpg" },
        { alt: "Moodle Logo", uri: "/lms/moodle.png" },
        { alt: "Microsoft Teams Logo", uri: "/lms/teams.png" }
    ];

    const logos = useMemo(() => {
        return (
            <ul className={`flex flex-row items-center justify-center align-middle ${isSafari ? 'gap-3' : ''}`} role="list" aria-label="Learning Management System logos">
                {imgData.map((img, i) => (
                    <li key={i} className="list-none">
                        <img
                            alt={img.alt}
                            src={img.uri}
                            className={`${isSafari ? '' : 'mx-2'} shrink-0 object-contain opacity-80 min-w-6 min-h-6 w-6 h-6 ${img.alt === "D2L Logo" ? "size-6" : "size-6"
                                }`}
                            style={isSafari ? {} : { marginLeft: '0.5rem', marginRight: '0.5rem' }}
                        />
                    </li>
                ))}
            </ul>
        );
    }, []);

    return (
        <div className="w-full py-2">
            <div className="mx-auto max-w-4xl px-4">
                <div className="mx-auto w-fit">
                    {/* Pill-shaped container */}
                    <Link href="/lms-integrations" aria-label="Learn more about LTI 1.3 integrations with learning management systems">
                        <div className="flex cursor-pointer items-center rounded-full border border-gray-200 bg-white px-3 py-1 shadow-sm transition-shadow hover:shadow-md sm:px-6">
                            {/* Left side - Scrolling Icons */}
                            <div className="relative w-48 overflow-hidden">
                                <div
                                    className={`flex w-fit flex-row ${isSafari ? "overflow-visible" : "overflow-hidden"
                                        }`}
                                >
                                    {!isSafari && (
                                        <div className="flex motion-safe:animate-[scroll-triple_10s_linear_infinite]">
                                            {logos}
                                            {logos}
                                            {logos}
                                        </div>
                                    )}
                                    {isSafari && (
                                        <div className="flex flex-wrap">
                                            {logos}
                                        </div>
                                    )}
                                </div>
                                {!isSafari && (
                                    <>
                                        <div className="absolute inset-0 w-full bg-linear-to-r from-white to-transparent to-10%" aria-hidden="true"></div>
                                        <div className="absolute inset-0 w-full bg-linear-to-l from-white to-transparent to-10%" aria-hidden="true"></div>
                                    </>
                                )}
                            </div>
                            {/* Vertical divider */}
                            <div className="mx-3 h-3 w-px bg-gray-300 sm:mx-6" aria-hidden="true"></div>
                            {/* Right side - Text with arrow */}
                            <div className="flex items-center space-x-2 text-xs font-medium text-gray-700 sm:text-sm">
                                <span className="whitespace-nowrap">Integration Guide</span>
                                <svg
                                    className="size-4 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                    />
                                </svg>
                            </div>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
}

export function IntegrationsCarouselCompact() {
    const [isSafari] = useState(false);
    const imgData: { alt: string; uri: string }[] = [
        { alt: "Aptem LMS Logo", uri: "/lms/aptem.png" },
        { alt: "Blackboard Logo", uri: "/lms/bb.png" },
        { alt: "Brightspace Logo", uri: "/lms/brightspace.png" },
        { alt: "Canvas Logo", uri: "/lms/canvas_circle.png" },
        { alt: "Google Classroom Logo", uri: "/lms/gc.jpeg" },
        { alt: "D2L Logo", uri: "/lms/d2l.jpg" },
        { alt: "Moodle Logo", uri: "/lms/moodle.png" },
        { alt: "Microsoft Teams Logo", uri: "/lms/teams.png" }
    ];

    const logos = useMemo(() => {
        return (
            <ul className={`flex flex-row items-center justify-center align-stretch gap-1`} role="list" aria-label="Learning Management System logos">
                {imgData.map((img, i) => (
                    <li key={i} className="list-none">
                        <img
                            alt={img.alt}
                            src={img.uri}
                            className={`shrink-0 object-contain opacity-80 min-w-4 min-h-4 w-4 h-4 ${img.alt === "D2L Logo" ? "size-4" : "size-4"}`}
                        />
                    </li>
                ))}
            </ul>
        );
    }, []);

    return (
        <div className="w-full -mb-8">
            <div className="container mx-auto px-4 lg:px-8">
                <div className="w-fit">
                    {/* Compact pill-shaped container */}
                    <Link href="/lms-integrations" aria-label="Learn more about LTI 1.3 integrations with learning management systems">
                        <div className="flex cursor-pointer items-center rounded-full border border-gray-200 bg-white px-2 py-0 shadow-sm transition-shadow hover:shadow-md sm:px-3">
                            {/* Left side - Compact Scrolling Icons */}
                            <div className="relative w-28 overflow-hidden">
                                <div
                                    className={`flex w-fit flex-row ${isSafari ? "overflow-visible" : "overflow-hidden"
                                        }`}
                                >
                                    {!isSafari && (
                                        <div className="flex motion-safe:animate-[scroll-triple_10s_linear_infinite]">
                                            {logos}
                                            {logos}
                                            {logos}
                                        </div>
                                    )}
                                    {isSafari && (
                                        <div className="flex flex-wrap">
                                            {logos}
                                        </div>
                                    )}
                                </div>
                                {!isSafari && (
                                    <>
                                        <div className="absolute inset-0 w-full bg-linear-to-r from-white to-transparent to-10%" aria-hidden="true"></div>
                                        <div className="absolute inset-0 w-full bg-linear-to-l from-white to-transparent to-10%" aria-hidden="true"></div>
                                    </>
                                )}
                            </div>
                            {/* Vertical divider */}
                            <div className="mx-2 h-1.5 w-px bg-gray-300 sm:mx-3" aria-hidden="true"></div>
                            {/* Right side - Compact Text with arrow */}
                            <div className="flex items-center space-x-1 text-xs font-medium text-gray-700">
                                <span className="whitespace-nowrap">Integration Guide</span>
                                <svg
                                    className="size-3 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                    />
                                </svg>
                            </div>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
}